import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewEncapsulation,
} from "@angular/core";
import { Balance, LimitsModalType, LimitsSummary } from "@obg/ngx.core";
import { fromMillisecondsToHours, fromMillisecondsToMinutes } from "@obg/ngx.global";
import { Observable, of, Subscription } from "rxjs";
import { delay, tap } from "rxjs/operators";

import { LastLoginTimestamp } from "../../common.login/login/last-login/last-login.model";
import { LastLoginService } from "../../common.login/login/last-login/last-login.service";
import { LimitsReminderConfigService } from "../sdk/limits-reminder.config";

@Component({
	selector: "obg-m-limits-reminder",
	templateUrl: "./limits-reminder.component.html",
	styleUrls: ["./limits-reminder.component.scss"],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LimitsReminderComponent implements OnChanges, OnInit, OnDestroy {
	@Input() limitsModalType: LimitsModalType | undefined;
	@Input() limitsSummary: LimitsSummary | undefined;
	@Input() balance: Balance | undefined;

	@Output() closeEvent = new EventEmitter();
	@Output() redirectToResponsibleGamingEvent = new EventEmitter();

	isShowAfterLogout = false;
	isLastLoginTimestampEnabled = false;
	usedSessionLimitDurationHours: string | undefined;
	usedSessionLimitDurationMinutes: string | undefined;
	sessionLimitDurationHours: number | undefined;
	limitReminderTimeout: number;

	lastLoginTimestamp$: Observable<LastLoginTimestamp> | undefined;

	private closeModal$$: Subscription | undefined;

	constructor(
		private configService: LimitsReminderConfigService,
		private lastLoginService: LastLoginService
	) {
		this.isLastLoginTimestampEnabled = this.configService.isLastLoginTimestampEnabled();
		this.limitReminderTimeout = this.configService.getLimitsModalTimeout();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.limitsSummary.currentValue) {
			this.formatLimitSummarySession(changes.limitsSummary.currentValue);
		}
	}

	ngOnInit() {
		this.lastLoginTimestamp$ = this.lastLoginService.getLastLoginTimestamp$();

		if (this.limitsModalType === LimitsModalType.logout) {
			this.closeModalAfterTimeout$$();
		}
	}

	ngOnDestroy() {
		this.closeModal$$?.unsubscribe();
	}

	formatLimitSummarySession(limitsSummary: LimitsSummary) {
		const { sessionLimitInfoResponse } = limitsSummary;

		this.sessionLimitDurationHours = fromMillisecondsToHours(sessionLimitInfoResponse.sessionTimeLimit);
		this.usedSessionLimitDurationHours = Math.floor(
			fromMillisecondsToHours(sessionLimitInfoResponse.usedSessionTimeLimit)
		).toFixed(0);
		this.usedSessionLimitDurationMinutes = (
			fromMillisecondsToMinutes(sessionLimitInfoResponse.usedSessionTimeLimit) % 60
		).toFixed(0);
	}

	closeModal() {
		this.closeEvent.emit();
	}

	redirectToResponsibleGaming() {
		this.redirectToResponsibleGamingEvent.emit();
	}

	onCloseAnchorClick(nodeName: string) {
		if (nodeName === "A") {
			this.closeModal();
		}
	}

	showLastLoginTimestamp(): boolean {
		if (!this.isLastLoginTimestampEnabled) {
			return false;
		}

		if (this.limitsModalType !== LimitsModalType.login) {
			return false;
		}

		return true;
	}

	private closeModalAfterTimeout$$(): Subscription {
		return (this.closeModal$$ = of(this.limitsModalType)
			.pipe(
				delay(this.limitReminderTimeout),
				tap(() => this.closeModal())
			)
			.subscribe());
	}
}
